/**
 * Shared domain types.
 *
 * Imported by both the Worker (src/) and the SPA (web/). Keep this file free of any
 * runtime import so it stays erasable and safe in both build targets.
 */

export type EventStatus = "draft" | "live" | "ended";

export type MediaStatus = "pending" | "approved" | "rejected";

/**
 * Where a piece of media stands with Workers AI.
 *
 * `none` means the event did not ask for analysis. `queued` means it will
 * happen after the response returns. `skipped` is a deliberate non-analysis —
 * a video, an event over its free budget, or a deployment with no AI binding —
 * and is kept distinct from `failed`, which means the model was tried and did
 * not answer. A host debugging an untagged photo needs to tell those apart.
 */
export type MediaAiState = "none" | "queued" | "done" | "failed" | "skipped";

export type HostTier = "free" | "basic" | "pro";

/** What kind of workspace this is. A personal workspace has exactly one seat. */
export type WorkspaceKind = "personal" | "team";

/**
 * What a member may do.
 *
 *   owner   the billing owner; can do everything and is the last line of defence
 *           against a workspace being left with nobody in charge
 *   admin   can manage people, invites and every event
 *   member  can create and run events, but not manage the team
 */
export type WorkspaceRole = "owner" | "admin" | "member";

/**
 * A workspace is the thing that owns events and pooled storage.
 *
 * Every host has a personal workspace, so there is one authorisation path rather
 * than an "is this a team?" branch in every route. `role` is the caller's role and
 * is only present on a workspace returned to a signed-in host.
 */
export interface WorkspaceBase {
  readonly workspaceId: string;
  readonly name: string;
  readonly kind: WorkspaceKind;
  /** The billing owner. Always a member with role "owner". */
  readonly ownerEmail: string;
  readonly plan: string;
  readonly seatLimit: number;
  readonly memberCount: number;
  /** Pooled storage bought, in bytes. */
  readonly credits: number;
  /** Pooled storage currently in use, in bytes. */
  readonly storageBytes: number;
  readonly createdAt: string;
}

export interface Workspace extends WorkspaceBase {
  /** The caller's role in this workspace. */
  readonly role: WorkspaceRole;
}

export interface WorkspaceMember {
  readonly email: string;
  readonly displayName: string;
  readonly role: WorkspaceRole;
  readonly joinedAt: string;
}

export interface WorkspaceInvite {
  readonly inviteId: string;
  readonly role: WorkspaceRole;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly expiresAt: string;
}

export interface WorkspaceDetail {
  readonly workspace: Workspace;
  readonly members: readonly WorkspaceMember[];
  /** Only returned to owners and admins; members see an empty list. */
  readonly invites: readonly WorkspaceInvite[];
}

/** Per-event capability flags. Mirrors the Events sheet boolean columns. */
export interface EventTheme {
  readonly primaryColor: string;
  readonly backgroundColor: string;
  readonly backgroundImageUrl: string | null;
  readonly backgroundMusicUrl: string | null;
  readonly layout: "grid" | "masonry";
  readonly showGuestNames: boolean;
  /**
   * Show a scannable QR card on the projector wall.
   *
   * Defaults to true: the wall has no other instruction for guests, and the QR
   * is the cheapest way to get a room full of people onto the event page. The
   * stored JSON is merged over DEFAULT_THEME, so existing events get it without
   * a migration.
   */
  readonly showQrOnWall: boolean;
  /** Caption under the wall QR. Empty means "Scan to add your photos". */
  readonly qrCaption: string;
  /**
   * The wall's presentation. `grid` is the historic behaviour and the default,
   * so deploying this never changes a projector that is already running.
   */
  readonly wallMode: "grid" | "slideshow";
  /** Dwell time per photograph in slideshow mode, clamped to 3-30 seconds. */
  readonly slideDurationMs: number;
  /** Maximum display time per video in slideshow mode, clamped to 3-120 seconds. */
  readonly videoSlideDurationMs: number;
  /** How one photograph gives way to the next in slideshow mode. */
  readonly slideTransition: SlideTransition;
  /** Captions on the wall, independent of guest-name attribution. */
  readonly showCaptionsOnWall: boolean;
}

/**
 * The slide transitions a host can pick. Adding one means a CSS pair in
 * styles.css, an option in EventControl, and an entry here.
 */
export const SLIDE_TRANSITIONS = ["fade", "slide", "zoom", "focus", "depth", "parallax"] as const;
export type SlideTransition = (typeof SLIDE_TRANSITIONS)[number];

/** Untrusted JSON to a known transition. Anything unrecognised is a crossfade. */
export function normalizeSlideTransition(value: unknown): SlideTransition {
  return (SLIDE_TRANSITIONS as readonly unknown[]).includes(value) ? (value as SlideTransition) : "fade";
}

/**
 * Keep slideshow timing safe at the boundary where untrusted JSON becomes a
 * theme value. A missing or malformed value uses the supplied fallback.
 */
export function clampWallDuration(value: unknown, fallback: number, min: number, max: number): number {
  const candidate = typeof value === "number" && Number.isFinite(value) ? value : fallback;
  return Math.min(max, Math.max(min, Math.trunc(candidate)));
}

export interface Event {
  readonly eventId: string;
  /**
   * The workspace that owns this event, and therefore its storage and access.
   *
   * `ownerEmail` below is the creator, not the boundary. An event created by a
   * staff member belongs to the team, and that member leaving must not take it.
   */
  readonly workspaceId: string;
  readonly slug: string;
  readonly guestCode: string;
  readonly ownerEmail: string;
  readonly title: string;
  readonly eventDate: string;
  readonly hasPassword: boolean;
  readonly theme: EventTheme;
  readonly feedEnabled: boolean;
  readonly requireApproval: boolean;
  readonly allowLikes: boolean;
  readonly allowComments: boolean;
  readonly allowAiTagging: boolean;
  readonly allowBackgroundMusic: boolean;
  /**
   * May a guest take the approved photographs home from the public gallery?
   *
   * Defaults to true. The gallery was already public to anyone holding the link
   * and downloads are the expected behaviour of a photo album; a host who wants
   * a view-only gallery turns it off per event.
   */
  readonly allowDownloads: boolean;
  /**
   * May guests leave text and voice notes for the hosts?
   *
   * Defaults to off. A comment box nobody asked for is a moderation problem, and
   * a microphone on a stranger's phone is a bigger ask than a like button, so the
   * host switches the guestbook on for their event.
   */
  readonly allowGuestbook: boolean;
  /**
   * Which dictionary the guest surfaces open in: "es", "fr", "de", "pt", or ""
   * for the browser's own preference. A hard-coded English experience asked a
   * wedding's Spanish half to guess at buttons; the host knows the crowd.
   */
  readonly guestLanguage: string;
  readonly status: EventStatus;
  readonly recurring: boolean;
  readonly mediaCount: number;
  /**
   * ISO instant after which the retention sweep deletes this event's media.
   *
   * Derived, not typed by the host: it is `closedAt + effective window`. Empty
   * while the event is live, because the clock starts when the event ends and
   * not before. Empty on an ended event means the row predates storage windows
   * and must not be swept.
   */
  readonly expiresAt: string;
  /**
   * Days the event's plan includes after it ends. 30 on Free, 365 on Party,
   * 730 on Wedding and 1095 on Signature. An extension adds to it.
   */
  readonly storageWindowDays: number;
  /**
   * A shorter window the host deliberately chose, or 0 to use the plan window.
   * Never greater than `storageWindowDays`; the endpoint clamps it.
   */
  readonly retentionDays: number;
  /** ISO instant the event ended and the storage window started. Empty while live. */
  readonly closedAt: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Event as stored in Sheets. Passwords are hashed and never leave the Worker. */
export interface EventRecord extends Event {
  readonly passwordHash: string;
  readonly passwordSalt: string;
}

/**
 * One detected face, as fractions of the analysed image.
 *
 * Everything is 0..1 rather than pixels: the boxes are produced from the
 * AI thumbnail but clients crop them out of whatever derivative they have,
 * and fractional boxes survive the client picking a different resolution.
 */
export interface FaceBox {
  readonly xMin: number;
  readonly yMin: number;
  readonly xMax: number;
  readonly yMax: number;
}

export interface Media {
  readonly mediaId: string;
  readonly eventId: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly width: number;
  readonly height: number;
  readonly durationSec: number;
  readonly caption: string;
  readonly guestName: string;
  readonly tags: readonly string[];
  readonly status: MediaStatus;
  readonly pinned: boolean;
  readonly likes: number;
  readonly blurred: boolean;
  readonly createdAt: string;
  /** Signed, short-lived display URL. Never the raw R2 key. */
  readonly displayUrl: string;
  /**
   * A small derivative for grids and projectors. Falls back to `displayUrl`
   * for older media and for clients that could not render one, so no surface
   * has to branch on whether it exists.
   */
  readonly thumbUrl: string;
  /** Model-written description. Never substitutes for the guest's caption. */
  readonly aiCaption: string;
  readonly aiState: MediaAiState;
  /** null = not analysed; true = family-safe; false = blurred pending review. */
  readonly aiSafe: boolean | null;
  /**
   * The album this photograph belongs to, or null for the event's "everything"
   * view. Null is the default forever: the historic gallery is the event itself,
   * and albums are an overlay on top of it, not a replacement.
   */
  readonly albumId: string | null;
  /**
   * Face boxes detected by Workers AI at analysis time, as fractions of the
   * analysed image (the thumbnail), so any client can crop a face without
   * knowing the derivative's pixel dimensions. Empty for unanalysed media.
   */
  readonly faces: readonly FaceBox[];
}

export interface MediaRecord extends Media {
  readonly r2Key: string;
}

export interface Comment {
  readonly commentId: string;
  readonly mediaId: string;
  readonly author: string;
  readonly body: string;
  readonly createdAt: string;
}

export type GuestbookStatus = "pending" | "approved" | "rejected";

/** The host's moderation list: entries plus per-status counts for the tabs. */
export interface GuestbookListResponse {
  readonly entries: readonly GuestbookEntry[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
  /** Host callers only. */
  readonly counts?: Readonly<Record<GuestbookStatus, number>>;
}

/**
 * One note in the guestbook: a text message or a voice recording.
 *
 * Audio notes are projected with an `audioUrl` the guest surfaces can play
 * directly, mirroring how `Media.displayUrl` works for photographs. The R2
 * key behind it never crosses the wire.
 */
export interface GuestbookEntry {
  readonly entryId: string;
  readonly eventId: string;
  readonly kind: "text" | "audio";
  /** The note's text. Empty for an audio entry. */
  readonly body: string;
  /** Playable URL, set only for audio notes. */
  readonly audioUrl?: string;
  readonly mimeType: string;
  readonly durationSec: number;
  readonly sizeBytes: number;
  readonly guestName: string;
  readonly createdAt: string;
  /**
   * Moderation state. Guests only ever receive approved notes, plus their own
   * in any state (flagged `mine`), so a guest can see "waiting for the host".
   */
  readonly status: GuestbookStatus;
  /**
   * True when the *caller's own* guest key matches this entry's authorship
   * hash, so the UI can show a delete affordance that will not end in a 403.
   * Computed per request in the list route; the entry has no other way to know.
   */
  readonly mine?: boolean;
}

/** A named collection inside one event, e.g. "The Ceremony" or "Best ones of me". */
export interface GalleryAlbum {
  readonly albumId: string;
  readonly eventId: string;
  readonly name: string;
  /** Small ordinal the host can reorder by. */
  readonly position: number;
  /** How many approved photographs sit in the album; filled on read. */
  readonly mediaCount: number;
  readonly createdAt: string;
}

/**
 * A custom hostname mapped to one event.
 *
 * `mode` picks which surface the hostname opens: the gallery, or the wall. The
 * wall form redirects with its `?k=` token, so it is a link printed on signage,
 * which is what wall links already are.
 */
export interface EventDomain {
  readonly domain: string;
  readonly eventId: string;
  readonly mode: "gallery" | "wall";
  /** ISO instant this Worker last answered a verification probe for the domain. */
  readonly verifiedAt: string;
  readonly createdAt: string;
}

/* ------------------------------------------------------------------ */
/* Host surfaces                                                       */
/* ------------------------------------------------------------------ */

/** GET /api/events/:slug/share. */
export interface ShareResponse {
  readonly links: ShareLinks;
  /**
   * Absolute origin for printed material: a verified custom domain, else
   * APP_ORIGIN. Empty when neither is configured, and the caller then falls
   * back to its own origin and says so.
   */
  readonly printOrigin: string;
}

export interface ShareLinks {
  readonly slug: string;
  /** Short code printed on signage. Possession of it is guest authority. */
  readonly guestCode: string;
  /** Relative paths. The browser resolves them against its own origin. */
  readonly guestPath: string;
  readonly wallPath: string;
  readonly wallCode: string;
  readonly eventPath: string;
}

export interface ModerationSummary {
  readonly pending: number;
  readonly approved: number;
  readonly rejected: number;
}

export interface ModerationQueueResponse {
  readonly event: Event;
  readonly summary: ModerationSummary;
  readonly media: readonly Media[];
}

export interface TimeBucket {
  /** ISO hour, e.g. "2026-10-14T19". */
  readonly bucket: string;
  readonly count: number;
}

export interface Contributor {
  readonly guestName: string;
  readonly count: number;
  readonly bytes: number;
}

export interface EventAnalytics {
  readonly eventId: string;
  readonly slug: string;
  readonly totalMedia: number;
  readonly approved: number;
  readonly pending: number;
  readonly rejected: number;
  readonly totalBytes: number;
  readonly images: number;
  readonly videos: number;
  readonly likes: number;
  readonly comments: number;
  readonly contributors: readonly Contributor[];
  /** Uploads per hour across the event's whole life. */
  readonly overTime: readonly TimeBucket[];
  readonly firstUploadAt: string;
  readonly lastUploadAt: string;
}

/* ------------------------------------------------------------------ */
/* API request/response contracts                                     */
/* ------------------------------------------------------------------ */

export interface CreateEventRequest {
  /** Omit for the caller's personal workspace. Must be a workspace they belong to. */
  readonly workspaceId?: string;
  readonly title: string;
  readonly eventDate: string;
  readonly password?: string;
  readonly theme?: Partial<EventTheme>;
  readonly requireApproval?: boolean;
  readonly allowLikes?: boolean;
  readonly allowComments?: boolean;
  readonly feedEnabled?: boolean;
  readonly allowAiTagging?: boolean;
  /** Omitted means true; only an explicit false makes a new gallery view-only. */
  readonly allowDownloads?: boolean;
  /** Omitted means off: the host switches the guestbook on per event. */
  readonly allowGuestbook?: boolean;
  /** "" follows the browser's language; otherwise a shipped dictionary code. */
  readonly guestLanguage?: string;
}

export interface CreateEventResponse {
  readonly event: Event;
}

export interface UploadTicketRequest {
  readonly guestCode: string;
  readonly filename: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly width?: number;
  readonly height?: number;
  readonly durationSec?: number;
}

export interface UploadTicketResponse {
  readonly ticketId: string;
  readonly uploadUrl: string;
  readonly r2Key: string;
  readonly expiresAt: string;
}

export interface UploadCompleteRequest {
  readonly ticketId: string;
  readonly caption?: string;
  readonly guestName?: string;
}

export interface UploadCompleteResponse {
  readonly media: Media;
}

export interface LiveEventMessage {
  readonly type: "media" | "heartbeat" | "snapshot" | "removed" | "updated";
  readonly media?: Media;
  readonly mediaId?: string;
  readonly mediaCount?: number;
}

/**
 * The live wall's initial paint.
 *
 * Sent once so a projector that connects mid-event shows a full grid rather than
 * an empty screen waiting for the next upload. Bounded by the same ring-buffer
 * size the Durable Object keeps, so it cannot be used to pull an entire event's
 * history out of the database.
 */
export interface WallPayload {
  readonly event: Pick<
    Event,
    | "slug"
    | "title"
    | "theme"
    | "feedEnabled"
    | "allowLikes"
    | "allowComments"
    | "allowBackgroundMusic"
    | "status"
    | "guestLanguage"
  >;
  /**
   * The guest link the wall's QR overlay encodes, e.g. "/u/ab3kwx".
   *
   * The projector has the wall token but not the guest code, and the QR is
   * exactly how the wall turns viewers into uploaders, so the wall payload has
   * to bring the answer with it. Anyone who can read this payload already holds
   * a valid wall token, which is a stronger credential than the six-letter
   * guest code it reveals.
   */
  readonly guestPath: string;
  readonly media: readonly Media[];
  readonly totalCount: number;
}

/**
 * Is anybody actually watching the wall?
 *
 * A wall that has silently stopped updating looks exactly like an event nobody is
 * uploading to, and those are two very different problems with opposite fixes. A
 * host who cannot tell them apart will blame the guests. This is the number that
 * tells them apart.
 *
 * `watchers` counts live connections to the room, which includes the host's own
 * moderation queue — so read it as "the room has subscribers", not "a television
 * is plugged in". `lastActivityAt` is zero when the Durable Object has just been
 * instantiated and nothing has happened yet, which is the state worth telling the
 * host about: a room that is quiet *and* newly woken has no wall on it at all.
 */
export interface WallStatus {
  readonly watchers: number;
  readonly buffered: number;
  /** Epoch ms of the last change, or 0 if the room has not seen one. */
  readonly lastActivityAt: number;
}

export interface UpdateMediaRequest {
  readonly status?: MediaStatus;
  readonly pinned?: boolean;
  readonly blurred?: boolean;
  readonly caption?: string;
  /** Move into an album, or null to return to the event's everything view. */
  readonly albumId?: string | null;
}

export interface ModerationBulkRequest {
  readonly action: "approve" | "reject" | "blur" | "unblur" | "delete";
  /** Omit to apply to the whole pending queue. */
  readonly mediaIds?: readonly string[];
}

export interface LikeResponse {
  readonly liked: boolean;
  readonly likes: number;
}

export interface CommentRequest {
  readonly body: string;
  readonly guestName?: string;
  /** Proves the commenter holds a guest link. Enables self-deletion. */
  readonly guestCode?: string;
  /** Stable per-browser id so one device does not inflate a count. */
  readonly likerKey?: string;
}

export interface RetentionRequest {
  /**
   * ISO instant, or null/"" to fall back to the plan window.
   *
   * Kept for older clients. A shorter day count is the supported form; an
   * absolute instant is converted to days from `closedAt` and clamped to the
   * plan's included window.
   */
  readonly expiresAt?: string | null;
  /** Shorter window in days. Clamped to 1..the event's plan window. */
  readonly days?: number;
}

/* ------------------------------------------------------------------ */
/* Errors                                                              */
/* ------------------------------------------------------------------ */

export type ApiErrorCode =
  | "bad_request"
  | "not_found"
  | "unauthorized"
  | "forbidden"
  | "rate_limited"
  | "payload_too_large"
  | "unsupported_media_type"
  | "quota_exceeded"
  | "upstream_error"
  | "conflict"
  | "internal";

export interface ApiErrorBody {
  readonly error: {
    readonly code: ApiErrorCode;
    readonly message: string;
    readonly details?: Record<string, unknown>;
  };
}

/* ------------------------------------------------------------------ */
/* Billing                                                             */
/* ------------------------------------------------------------------ */

/**
 * What a host has bought and what they have spent.
 *
 * All three numbers are bytes. That is deliberate and it is the whole design:
 * a credit *is* a byte, so there is no conversion, no rate table, and no way for
 * the number on the dashboard and the number in the bucket to disagree about
 * what they mean. The Dodo side carries money; this side carries bytes.
 */
export interface BillingSummary {
  /** The workspace whose pool this is. */
  readonly workspaceId: string;
  /** Purchased, not yet spent. */
  readonly creditBytes: number;
  /** Everything currently in this host's buckets. */
  readonly storageBytes: number;
  /** What they could upload right now, free allowance included. */
  readonly allowedBytes: number;
  /** allowedBytes - storageBytes. Never negative; the upload gate refuses first. */
  readonly remainingBytes: number;
  /** Storage granted before any purchase. */
  readonly freeAllowanceBytes: number;
  /** True once purchased credits cover the allowance, i.e. they have paid. */
  readonly hasPaid: boolean;
  /** False until `npm run billing:sync` has created the Dodo products. */
  readonly checkoutReady: boolean;
}

export interface CreditPack {
  readonly slug: string;
  readonly name: string;
  readonly bytes: number;
  /** Days of storage after the event ends that this pack includes. */
  readonly windowDays: number;
  readonly priceLabel: string;
  /** False when the Dodo product this maps to does not exist yet. */
  readonly available: boolean;
}

export interface CreditLedgerEntry {
  readonly id: number;
  readonly deltaBytes: number;
  readonly balanceAfter: number;
  readonly reason: string;
  readonly reference: string | null;
  readonly createdAt: string;
}

/* ------------------------------------------------------------------ */
/* Limits — single source of truth, enforced Worker-side               */
/* ------------------------------------------------------------------ */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MEGABYTE = 1_048_576;
const GIGABYTE = 1024 * MEGABYTE;

export const LIMITS = {
  /** Presigned PUT URLs are short-lived on purpose. */
  ticketTtlMs: 5 * MINUTE,
  /** Uploads per guest device per hour, per event. */
  uploadsPerGuest: 50,
  /**
   * Uploads per hour across a whole event. The backstop for a client that
   * invents a new device key per request; well above a 300-guest reception.
   */
  uploadsPerEventPerHour: 2000,
  /** Per-file cap on the free tier. */
  maxFileBytesFree: 25 * MEGABYTE,
  maxFileBytesPro: 200 * MEGABYTE,
  /**
   * Longest edge of the client-rendered thumbnail, in pixels. 720 is enough for
   * a 1080p wall tile and a retina phone grid, while keeping a typical
   * thumbnail under 100 KB. The original is always kept for the lightbox.
   */
  thumbMaxEdge: 720,
  /** A thumbnail larger than this is not a thumbnail; refuse it at the Worker. */
  maxThumbBytes: 256 * 1024,
  /** Reject absurd dimensions early; these are decoded client-side. */
  maxPixels: 50_000_000,
  /** Ring buffer size for the live wall replay. */
  feedBufferSize: 30,
  maxConcurrentSubscribers: 200,
  /** SSE heartbeat interval. */
  heartbeatMs: 15 * 1000,

  /* ---- Engagement ---- */
  maxCommentChars: 500,
  maxCommentsPerMedia: 200,
  maxCaptionChars: 500,

  /* ---- Guestbook ---- */
  /** A note is a message, not a letter. Same budget as a comment. */
  maxGuestbookChars: 500,
  /**
   * 5 MB of Opus/AAC at 32-64 kbps is several minutes of speech, so the
   * duration cap below is what binds; the byte cap only stops a lying client.
   */
  maxGuestbookAudioBytes: 5 * MEGABYTE,
  /** A voice note is a toast, not a speech. The recorder stops itself here. */
  maxGuestbookAudioSeconds: 60,
  /**
   * Posting limits, per event. The device limit is the real one; the address
   * limit is deliberately loose because a whole wedding shares the venue's
   * Wi-Fi and therefore one public IP.
   */
  guestbookDevicePosts: 5,
  guestbookDeviceWindowMs: 10 * 60_000,
  guestbookDeviceDailyPosts: 20,
  guestbookAddressPosts: 60,
  guestbookAddressWindowMs: 10 * 60_000,
  /** Browsers record one of these; Safari's MediaRecorder says mp4. */
  guestbookAudioMime: ["audio/webm", "audio/mp4"] as const,

  /* ---- Albums ---- */
  /** Twenty is a coherent afternoon of sorting, not a wall of dropdowns. */
  maxAlbumsPerEvent: 20,
  maxAlbumNameChars: 40,

  /* ---- Custom domains ---- */
  /** One hostname for the gallery and one for the wall. */
  maxDomainsPerEvent: 2,
  maxDomainsPerWorkspace: 10,
  domainClaimsPerHour: 5,
  /** One "move N photos" request may touch at most this many rows. */
  maxMediaPerAlbumOp: 200,

  /* ---- Team invitations ---- */
  /** Unsent invites accumulate as a small abuse surface; keep the list finite. */
  maxPendingInvites: 25,

  /* ---- Face search ---- */
  /** Cap per image so a crowd scene cannot produce an unusable crop strip. */
  maxFacesPerMedia: 8,

  /* ---- Moderation ---- */
  /** A host works a queue; 200 is a full day of photos on the free tier. */
  maxQueuePage: 200,

  /* ---- Export ---- */
  /**
   * A classic (non-ZIP64) archive is capped at 4 GB with 65,535 entries. The
   * export stops at this byte budget and says so in a manifest rather than
   * emitting a file that unarchivers silently reject.
   */
  maxExportBytes: 3_500_000_000,
  maxExportFiles: 2_000,
  exportChunkBytes: 1_048_576,

  /* ---- Guest downloads ---- */
  /**
   * A guest may choose a subset of an event's approved media. 100 is the point
   * at which a phone can still show a selection meaningfully and the URL stays
   * well inside what every browser accepts.
   */
  maxGuestDownloadFiles: 100,
  /** Fixed window for public egress: this many requests per IP and event. */
  guestDownloadRequests: 5,
  guestDownloadWindowMs: 10 * 60_000,
  /** How long a rate-limit row is worth keeping. */
  guestDownloadRowTtlMs: 24 * 60 * 60_000,

  /* ---- Retention ---- */
  /** Window included with a free event. Paid plans raise it. */
  defaultRetentionDays: 30,
  /** A host may delete earlier than the plan window, but never to zero. */
  minRetentionDays: 1,
  /** Absolute ceiling for a malformed row; plan windows are the real limit. */
  maxRetentionDays: 3650,

  /* ---- AI enrichment ---- */
  /**
   * How many images per event are sent to Workers AI.
   *
   * The free allocation is 10,000 Neurons/day for the whole account. At the
   * measured cost of a 720px thumbnail plus a short structured answer, 150
   * images is comfortably inside one day's allowance for a single event, and
   * leaves headroom for other events running at the same time. Raising this is
   * the first thing to do after moving to the $5 Workers plan.
   */
  aiImagesPerEvent: 150,
  aiCaptionChars: 200,
  aiTagsPerMedia: 8,

  /* ---- Lifecycle email ---- */
  /**
   * How many times the drain may try a message before it gives up.
   *
   * Six attempts across the backoff schedule below spans roughly a day, which
   * covers a bad deploy, a brief Email Sending outage, and the usual transient
   * DNS complaint. Past that a message is almost certainly undeliverable, and
   * keeping it in the outbox only hides the failure.
   */
  emailMaxAttempts: 6,
  /** Messages claimed per drain pass. Keeps one cron run inside a request budget. */
  emailBatchSize: 25,
  /**
   * Retry delays, indexed by attempt count. The last value repeats. Sending is
   * asynchronous by design, so a delay costs nothing but a later cron pass.
   */
  emailRetryMinutes: [5, 15, 60, 180, 360] as const,
  /**
   * How long to hold mail once the provider's daily allowance is used up.
   * Apps Script resets 24 hours after the first send of the day; six hours
   * means at most four quiet hourly passes before the next probe.
   */
  emailQuotaDeferMinutes: 360,
  /**
   * A claim older than this is presumed abandoned (the isolate died mid-send)
   * and may be reclaimed. Comfortably longer than one send could take.
   */
  emailClaimStaleMinutes: 15,
  /** Days after an event ends before the single review request is due. */
  emailReviewDelayDays: 3,
  /** Review requests, welcomes and "ready" mail stop being useful after this. */
  emailWelcomeDiscardDays: 30,
  emailReadyDiscardDays: 14,
  emailReviewDiscardDays: 14,
  /** Lead times for the two deletion warnings. Both are measured from expires_at. */
  emailRetentionLeadDays: [7, 1] as const,

  /* ---- Billing ---- */
  /**
   * Storage a host gets before paying anything.
   *
   * 500 MB is ideal for testing and intimate dinners (~100-120 high-res photos)
   * while keeping the R2 free tier safe and driving high conversion to paid passes.
   */
  freeStorageBytes: 500 * MEGABYTE,
  /**
   * How far back a webhook timestamp may be before it is refused.
   *
   * Bounds replay: a captured, valid signature is only useful while the
   * timestamp is fresh, because the payload cannot be altered without breaking
   * it. Standard Webhooks uses five minutes; long enough to survive a deploy and
   * a retry, short enough that a leaked request body is worthless an hour later.
   */
  webhookToleranceSec: 300,
} as const;


/* ------------------------------------------------------------------ */
/* Storage windows — shared by the Worker and every host surface       */
/* ------------------------------------------------------------------ */

/**
 * Days after the event date at which an event closes itself.
 *
 * The pricing page promises a window that starts when the event ends, so
 * "ends" has to be a date the product can derive without the host remembering.
 * Fourteen days leaves room for the morning-after uploads while making it
 * impossible to park an open event — and therefore never start its clock —
 * indefinitely.
 */
export const AUTO_CLOSE_DAYS = 14;

/** The free plan's window. Also the default for an event with no paid pass. */
export const FREE_STORAGE_WINDOW_DAYS = 30;

/** How many days an extension pack adds. The catalog's own 12-month window. */
export const EXTENSION_STORAGE_WINDOW_DAYS = 365;

export type RetentionWarning = "none" | "30d" | "7d" | "expired";

/**
 * When an event ends itself: `event_date + 14 days`, at midnight UTC.
 *
 * Returns "" for a missing or malformed date, which means "no auto-close". The
 * host can still end the event by hand, and a draft with no date must not be
 * given a clock it never asked for.
 */
export function autoCloseInstant(eventDate: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(eventDate)) return "";
  const start = Date.parse(`${eventDate}T00:00:00.000Z`);
  if (Number.isNaN(start)) return "";
  return new Date(start + AUTO_CLOSE_DAYS * DAY).toISOString();
}

/**
 * The number of days an event is actually kept after it ends.
 *
 * The plan window is the entitlement; the host may choose a shorter one. A
 * malformed 0 falls back to the free window rather than deleting immediately,
 * because "no window" is never what a host means.
 */
export function effectiveRetentionDays(
  storageWindowDays: number,
  retentionDays: number,
): number {
  const plan = Math.max(
    LIMITS.minRetentionDays,
    Math.trunc(storageWindowDays || FREE_STORAGE_WINDOW_DAYS),
  );
  const chosen = Math.trunc(retentionDays || 0);
  return chosen > 0 ? Math.min(plan, Math.max(LIMITS.minRetentionDays, chosen)) : plan;
}

/**
 * Which deletion warning a host should see, if any.
 *
 * The product promises a warning before anything disappears. T-30 and T-7 are
 * the two thresholds the pricing strategy commits to, and they are computed
 * from the stored expiry rather than from a scheduled email, so a host who
 * never opened an email still sees the same answer in the dashboard.
 */
export function retentionWarning(expiresAt: string, at = Date.now()): RetentionWarning {
  const parsed = Date.parse(expiresAt);
  if (!expiresAt || Number.isNaN(parsed)) return "none";

  const remaining = parsed - at;
  if (remaining <= 0) return "expired";
  if (remaining <= 7 * DAY) return "7d";
  if (remaining <= 30 * DAY) return "30d";
  return "none";
}

/** Whole days until an instant, rounded up so "1.2 days" reads as 2. */
export function daysUntilInstant(instant: string, at = Date.now()): number {
  const parsed = Date.parse(instant);
  if (!instant || Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.ceil((parsed - at) / DAY));
}

/** Human label for a plan window: 30 days, 12 months, 24 months, 36 months. */
export function storageWindowLabel(days: number): string {
  if (!Number.isFinite(days) || days <= 0) return "no storage window";
  if (days % 3650 === 0) {
    // 3650 days is ten plan years, not one. The grandfather backfill uses it.
    const years = days / 365;
    return `${years} ${years === 1 ? "year" : "years"}`;
  }
  if (days % 365 === 0) {
    // A plan year is 365 days, so 365 must read as 12 months, not one month.
    const months = Math.round(days / (365 / 12));
    return `${months} ${months === 1 ? "month" : "months"}`;
  }
  return `${days} ${days === 1 ? "day" : "days"}`;
}

export const ALLOWED_MIME = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "audio/webm",
  "audio/ogg",
  "audio/mp4",
  "audio/mpeg",
  "audio/wav",
] as const;

export type AllowedMime = (typeof ALLOWED_MIME)[number];

export function isAllowedMime(value: string): value is AllowedMime {
  return (ALLOWED_MIME as readonly string[]).includes(value);
}

export function isAllowedMimeKind(mime: AllowedMime): "image" | "video" | "audio" {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("audio/")) return "audio";
  return "video";
}
